import { useMemo } from "react";
import _ from "underscore";

import {
  Box,
  Divider,
  RoundedBox,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { useUser } from "@js/apps/common/hooks";
import { openOTPDisabledWarningModal } from "@js/apps/settings/components/otp-auth";
import { openTaxDocumentsModal } from "@js/apps/settings/components/tax-documents";
import { useGetWithdrawalMethodsQuery } from "@js/apps/withdrawal/api/api";
import { useOpenChooseWithdrawalMethodModal } from "@js/apps/withdrawal/hooks";
import { useNavigate } from "@js/hooks";
import { isNotNullable } from "@js/utils";

import {
  reviewInvoicingAction,
  reviewPaymentTermsAction,
} from "../../hooks/use-freelancer-action-items/const";

import type { ChecklistAction } from "./checklist-container";
import { ChecklistContainer } from "./checklist-container";
import { CHECKLIST_ITEMS } from "./const";

type OnboardingChecklistProps = {
  filterActions?: (actions: ChecklistAction[]) => ChecklistAction[];
};

export const OnboardingChecklist = ({
  filterActions,
}: OnboardingChecklistProps) => {
  const navigate = useNavigate();

  const {
    defaultWithdrawalMethod,
    isOTPEnabled,
    isW8FormRequired,
    isW9FormRequired,
    address,
    isTaxFormSubmitted,
  } = useOnboardingStatus();

  const { onChooseWithdrawalMethod } = useOpenChooseWithdrawalMethodModal();

  const ACTIONS: ChecklistAction[] = useMemo(() => {
    return [
      { ...reviewPaymentTermsAction, type: CHECKLIST_ITEMS.PAYMENT_TERMS },
      { ...reviewInvoicingAction, type: CHECKLIST_ITEMS.INVOICING },
      {
        type: CHECKLIST_ITEMS.TWO_FACTOR,
        header: "Set up 2FA before submitting your first invoice",
        isCompleted: isOTPEnabled,
        action: isOTPEnabled ? false : openOTPDisabledWarningModal,
      },
      {
        type: CHECKLIST_ITEMS.BANKING,
        header: "Set up banking information",
        isCompleted: !!defaultWithdrawalMethod,
        action: defaultWithdrawalMethod
          ? false
          : isOTPEnabled
            ? onChooseWithdrawalMethod
            : openOTPDisabledWarningModal,
      },
      {
        type: CHECKLIST_ITEMS.TAX_DOCS,
        header: "Add tax documentation to your account",
        isCompleted: isTaxFormSubmitted,
        action: address
          ? isW8FormRequired || isW9FormRequired
            ? () => openTaxDocumentsModal({ showSwitchFormButton: true })
            : undefined
          : () => navigate("/settings/payments/"),
      },
    ];
  }, [
    address,
    defaultWithdrawalMethod,
    isOTPEnabled,
    isTaxFormSubmitted,
    isW8FormRequired,
    isW9FormRequired,
    navigate,
    onChooseWithdrawalMethod,
  ]);

  const filteredActions = (
    filterActions ? filterActions(ACTIONS) : ACTIONS
  ).filter(isNotNullable);

  return (
    <div>
      <ChecklistContainer actions={filteredActions} />
    </div>
  );
};

export const SignUpBYOTStaticChecklist = () => {
  return (
    <RoundedBox sx={{ border: "1px solid var(--soft-beige)" }}>
      <Typography component="h2" variant="label" size="large">
        ✏️ Braintrust checklist
      </Typography>
      <Box sx={{ py: 2 }}>
        <Typography component="h3" variant="paragraph">
          Review payment terms
        </Typography>
        <Typography
          component="p"
          variant="paragraph"
          sx={{ color: "var(--grey-2)" }}
        >
          Review terms and conditions, payment terms, and frequency for this job
        </Typography>
      </Box>
      <Divider color="beige" />
      <Box sx={{ pt: 2 }}>
        <Typography component="h3" variant="paragraph">
          Review how invoicing works
        </Typography>
        <Typography
          component="p"
          variant="paragraph"
          sx={{ color: "var(--grey-2)" }}
        >
          Make sure you have all the info you need to submit your first invoice
        </Typography>
      </Box>
    </RoundedBox>
  );
};

export const useOnboardingStatus = () => {
  const user = useUser();
  const { data: withdrawalMethods = [] } = useGetWithdrawalMethodsQuery();
  const defaultWithdrawalMethod = useMemo(() => {
    return _.findWhere(withdrawalMethods, { is_default: true });
  }, [withdrawalMethods]);

  const isOTPEnabled = user?.is_otp_enabled || user?.is_passkey_enabled;
  const isW8FormRequired = user?.is_w8_form_required;
  const isW9FormRequired = user?.is_w9_form_required;
  const address = user?.address;
  const isTaxFormSubmitted =
    !!user?.address &&
    !(user?.is_w8_form_required || user?.is_w9_form_required);

  return {
    defaultWithdrawalMethod,
    isOTPEnabled,
    isW8FormRequired,
    isW9FormRequired,
    address,
    isTaxFormSubmitted,
  };
};
